import cx from 'classnames';
import { type CSSProperties, forwardRef, type ReactNode } from 'react';
import {
  calculateContainerStyle,
  calculateIconStyle,
} from '@components/floorPlan/utils';
import { type IconProps } from '@components/icon/Icon';
import { useSvgContentBounds } from '@shared/hooks/useSvgContentBounds';
import { TableStateOverlay } from './TableStateOverlay';
import styles from './FloorPlanTable.scss';
import { FloorPlanIcons } from '@components/icon/FloorPlanIcons';

const FloorPlanTableName = ({
  tableName,
  isDisabled = false,
}: {
  tableName: string;
  isDisabled?: boolean;
}) => (
  <span className={cx(styles.tableName, { [styles.isDisabled]: isDisabled })}>
    {tableName}
  </span>
);

interface FloorPlanTableComponentProps extends React.ComponentProps<'div'> {
  left: string;
  top: string;
  tableIconScale: number;
  children: ReactNode;
}

const FloorPlanTableComponent = forwardRef<
  HTMLDivElement,
  FloorPlanTableComponentProps
>(({ left, top, tableIconScale, children, className, style, ...rest }, ref) => (
  <div
    className={cx(styles.container, className)}
    style={{
      ...calculateContainerStyle({
        left,
        tableIconScale,
        top,
      }),
      ...style,
    }}
    ref={ref}
    {...rest}
  >
    {children}
  </div>
));
FloorPlanTableComponent.displayName = 'FloorPlanTable';

export type TableHighlightingMode = 'outline' | 'goldCircle' | 'pulse';

interface FloorPlanTableIconProps extends Omit<IconProps, 'name'> {
  hasError?: boolean;
  iconName: string;
  iconWidthScale: string;
  isComboHovered?: boolean;
  isHighlighted?: boolean;
  orientation: string;
  showHoverStroke?: boolean;
  tableIconScale: number;
  tableHighlightingMode?: TableHighlightingMode;
}

const FloorPlanTableIcon = ({
  hasError = false,
  iconName,
  iconWidthScale,
  isComboHovered = false,
  isHighlighted = false,
  orientation,
  showHoverStroke = true,
  style,
  tableIconScale,
  tableHighlightingMode = 'outline',
  ...rest
}: FloorPlanTableIconProps) => {
  const {
    containerRef,
    contentBounds,
    viewBoxWidth: viewBoxW,
    viewBoxHeight: viewBoxH,
  } = useSvgContentBounds();

  const iconStyle = calculateIconStyle({
    iconWidthScale,
    orientation,
    tableIconScale,
  });

  const { transform: iconRotation, ...iconSizeStyle } = iconStyle;

  const getStrokeColor = () => {
    if (hasError) return 'var(--red300)';
    if (isHighlighted) return 'var(--gold)';
    return 'transparent';
  };

  // Hover stroke is CSS-driven (via .iconContainer:hover in SCSS) to avoid
  // JS pointer-event tracking being unreliable on transformed elements.
  const canHover = showHoverStroke && !hasError && !isHighlighted;

  const icon = (
    <div
      ref={containerRef}
      className={cx(styles.iconRoot, { [styles.iconContainer]: canHover })}
      style={{
        transform: iconRotation,
        pointerEvents: showHoverStroke ? 'auto' : 'none',
      }}
    >
      <FloorPlanIcons
        name={iconName}
        style={{
          ...iconSizeStyle,
          ...style,
        }}
        testId={`icon-${iconName}`}
        {...rest}
      />
      {contentBounds && viewBoxW && viewBoxH && (
        <TableStateOverlay
          contentBounds={contentBounds}
          viewBoxWidth={viewBoxW}
          viewBoxHeight={viewBoxH}
          stroke={getStrokeColor()}
          showHoverStroke={canHover}
          forceHoverStroke={canHover && isComboHovered}
        />
      )}
    </div>
  );

  if (isHighlighted && tableHighlightingMode !== 'outline') {
    const iconHighlightCircleStyle = iconStyle.width
      ? ({
          '--iconWidth': `${Number(iconStyle.width) * 1.15}px`,
        } as CSSProperties)
      : {};

    const iconWrapperClassName = cx({
      [styles.goldCircle]: true,
      [styles.pulse]: tableHighlightingMode === 'pulse',
    });

    return (
      <div
        data-testid="floor-plan-icon-wrapper"
        className={iconWrapperClassName}
        style={iconHighlightCircleStyle}
      >
        {icon}
      </div>
    );
  }

  return icon;
};

export default Object.assign(FloorPlanTableComponent, {
  Icon: FloorPlanTableIcon,
  TableName: FloorPlanTableName,
});
